/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'

import ImportCSVContainer from '../ImportCSVContainer'
import ImportCSVTitle from '../ImportCSVTitle'
import ImportCSVBanner, { BANNER_TYPES } from '../ImportCSVBanner'
import ImportCSVContentUploadForm from '../ImportCSVContentUploadForm'
import ImportTeamRosterErrorDuplicates from './ImportTeamRosterErrorDuplicates'
import ImportCSVReport from '../ImportCSVReport'
import { useImportReport } from '../hooks/useImportReport'
import { useBeforeUnload } from '../../../hooks/useBeforeUnload'
import { useDialogTimeouts } from '../../../hooks/useDialogTimeouts'
import { decodeCsvFile } from './utils/decodeCsvFile'
import { importCsvLayout } from './utils/importCsvRows'
import { importRequestOutcome } from './utils/importRequestOutcome'

/**
 * Body of the IMPORT ROSTER modal. Mounted by `ImportTeamRoster`, and directly
 * by callers that supply their own trigger button.
 *
 * `orgId` / `isMultiTeamRoster` apply only to the Org screen's multi-team
 * import — the JSDoc makes that optionality visible to TypeScript callers,
 * which bare destructuring would otherwise infer as required.
 *
 * @param {Object} props
 * @param {boolean} [props.loading]
 * @param {(loading: boolean) => void} [props.setLoading]
 * @param {any} [props.importTemplate] Entry from `getConfig()`.
 * @param {string} [props.teamId]
 * @param {string} [props.teamName]
 * @param {string} [props.teamRosterId]
 * @param {() => void} [props.toggle] Closes the modal.
 * @param {() => void} [props.onSuccess]
 * @param {string} [props.orgId] Multi-team import only; omit for one team.
 * @param {boolean} [props.isMultiTeamRoster]
 * @param {'players' | 'teamOfficials'} [props.rosterType] Which roster the file fills.
 */
const ImportTeamRosterContent = ({
  loading,
  setLoading,
  importTemplate,
  teamId,
  teamName,
  teamRosterId,
  toggle,
  onSuccess = () => {},
  orgId,
  isMultiTeamRoster = false,
  rosterType = 'players',
}) => {
  const [success, setSuccess] = useState(false)
  const [file, setFile] = useState(null)
  const [error, setError] = useState(false)
  // The file the last upload sent and its decoded rows: the duplicate review
  // posts this same file, and reads these rows for its labels and rewrite.
  const [uploadedCsv, setUploadedCsv] = useState(null)
  const [shouldCheckForConflicts, setShouldCheckForConflicts] = useState(true)
  const [clearRosterBeforeImport, setClearRosterBeforeImport] = useState(false)
  // What the last 409 said (SN-4838): the rows to review, the rows that
  // failed, or both, and whether the review is open.
  const { report, isReviewing, receive, startReview, clear } = useImportReport()
  // Set before the upload goes out, so a second submit event sends nothing.
  const uploadInFlight = useRef(false)
  // Set when an upload ended without an answer: it may have imported. Only
  // choosing a file again clears it.
  const uploadOutcomeUnknown = useRef(false)
  // The success close and the wrong-file message clear end with this dialog.
  const timeouts = useDialogTimeouts()

  // Either request (this upload, or the review's confirm) may still be writing.
  useBeforeUnload(loading)

  const showReview = report !== null && isReviewing
  const isTeamOfficials = rosterType === 'teamOfficials'
  const submitUrl =
    teamId && teamRosterId
      ? `/teams/${teamId}/rosters/${teamRosterId}/${
          isTeamOfficials ? 'team-officials' : 'players'
        }/import/csv`
      : `/organizations/${orgId}/teams/${
          isTeamOfficials ? 'team-officials/importmulti' : 'importmulti'
        }/csv`

  useEffect(() => {
    return () => {
      setLoading(false)

      setSuccess(false)
      setFile(false)
      setError(false)
      setUploadedCsv(null)
      setShouldCheckForConflicts(true)
    }
  }, [])

  const onUploadComplete = (response) => {
    clear()
    setSuccess(response)
    setLoading(false)
    onSuccess()
    timeouts.schedule('close', toggle, 10000)
  }

  // A 409 from either pass. The report replaces whatever was on screen,
  // including the review that posted it. `droppedLines`: the rows the review
  // left out of its file, so the report can be numbered as the admin's file.
  const handleImportError = (e, droppedLines = []) => {
    setLoading(false)
    receive(e, droppedLines)
    // We specifically call the success handler here in the case of error, because
    // sometimes the API will report an error but still process an upload? I know.
    onSuccess()
    setFile(null)
  }

  const cancelReview = () => {
    setError(false)
    clear()
    setFile(null)
    setShouldCheckForConflicts(true)
    setUploadedCsv(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    if (!file || uploadInFlight.current || uploadOutcomeUnknown.current) {
      return
    }

    uploadInFlight.current = true
    // A wrong file's message clear must not remove what this upload says.
    timeouts.clear('clearError')
    setError(false)
    clear()
    setLoading(true)

    try {
      let rows
      try {
        rows = await decodeCsvFile(file)
      } catch (readError) {
        setError(
          `There was a problem reading your file | ${readError?.message}`
        )
        return
      }
      setUploadedCsv({ file, rows })

      const body = new FormData()
      body.append('file', file)
      body.append('conflicts', shouldCheckForConflicts ? 'check' : 'ignore')
      body.append('clear_roster', clearRosterBeforeImport)

      try {
        const response = await req(submitUrl, {
          body,
          method: 'POST',
          parseJSON: false,
          skipAutoContentType: true,
          // One attempt: with no rows to review this request imports the file,
          // so a replay after a lost response would import it twice.
          maxAttempts: 1,
          reportGlobalError: false,
        })

        onUploadComplete(response)
      } catch (requestError) {
        const outcome = importRequestOutcome(requestError)

        if (outcome === 'report') {
          handleImportError(requestError)
        } else if (outcome === 'unknown') {
          uploadOutcomeUnknown.current = true
          setFile(null)
          setError(t('common:importDuplicatesOutcomeUnknown'))
        } else {
          setError(
            `There was a problem importing your file | ${
              requestError?.message || JSON.stringify(requestError)
            }`
          )
        }
      }
    } finally {
      uploadInFlight.current = false
      setLoading(false)
    }
  }

  const onFileSelect = ({ target }) => {
    // A file dropped while a request runs must not replace the one it sent.
    if (
      !target ||
      !target.files ||
      target.files.length === 0 ||
      uploadInFlight.current
    ) {
      return
    }

    const file = target.files[0]

    if (
      /image\/svg\+xml/.test(file?.type) ||
      (!/csv/.test(file?.type) && !/\.csv$/.test(file?.name))
    ) {
      setError('Please select a CSV file.')
      timeouts.schedule('clearError', () => setError(false), 5000)
      return
    }

    uploadOutcomeUnknown.current = false
    setError(false)
    clear()
    setFile(file)
  }

  return (
    <ImportCSVContainer
      hasImportErrors={report !== null && report.kind !== 'none'}
    >
      <ImportCSVTitle
        importTemplate={importTemplate}
        success={success}
        onClose={toggle}
        closeDisabled={loading}
        title={showReview ? 'Duplicate Review' : undefined}
      />
      <div role='status'>
        {loading && !showReview && uploadedCsv ? (
          <ImportCSVBanner
            type={BANNER_TYPES.info.type}
            message={t('common:importDuplicatesImporting', {
              count: importCsvLayout(uploadedCsv.rows).dataRows.length,
            })}
          />
        ) : null}
      </div>
      {error && (
        <ImportCSVBanner type={BANNER_TYPES.error.type} message={error} />
      )}
      {showReview ? (
        <ImportTeamRosterErrorDuplicates
          conflicts={report.duplicates}
          teamName={teamName}
          csvRows={uploadedCsv?.rows ?? []}
          file={uploadedCsv?.file}
          resetFile={cancelReview}
          submitUrl={submitUrl}
          onUploadComplete={onUploadComplete}
          onImportError={handleImportError}
          setLoading={setLoading}
          isMultiTeamRoster={isMultiTeamRoster}
          problemRowCount={report.problemRowCount}
        />
      ) : report !== null ? (
        <ImportCSVReport report={report} onReviewDuplicates={startReview} />
      ) : null}
      {success ? (
        <>
          <ImportCSVBanner
            type={BANNER_TYPES.success.type}
            message={
              'Your team roster update was completed successfully. Dismissing this modal in 10 seconds...'
            }
          />
          <div
            css={css`
              border: 1px solid #3f4753;
              border-radius: 4px;
              padding: 10px;
            `}
          >
            {success?.success?.count} player(s) were imported.
          </div>
        </>
      ) : (
        !showReview && (
          <ImportCSVContentUploadForm
            onSubmit={onSubmit}
            loading={loading}
            onFileSelect={onFileSelect}
            resetFile={() => setFile(null)}
            file={file}
            importTemplate={importTemplate}
            error={error}
            clearRosterBeforeImport={clearRosterBeforeImport}
            setClearRosterBeforeImport={setClearRosterBeforeImport}
          />
        )
      )}
    </ImportCSVContainer>
  )
}

export default ImportTeamRosterContent
